import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { Sidebar } from "./Sidebar";
import { Footer } from "./Footer";
import { MobileThemeButton } from "./MobileThemeButton";
import { MfaWarningBanner } from "../MfaWarningBanner";
import { WashSaleBanner } from "../WashSaleBanner";
import { AdminCustomBanner } from "../AdminCustomBanner";
import { TokenRefreshBanner } from "../TokenRefreshBanner";
import { HelpChat } from "../HelpChat";

/**
 * Layout for public routes that don't require authentication.
 * Shows the full app UI but allows anonymous access.
 * Used for: /search, /backtest, /backtest/:id (community strategies)
 */
export function PublicLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Close mobile nav when navigating to a new page
  useEffect(() => {
    // This synchronous setState is intentional - we want to close the nav immediately on navigation
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  const pageTitle = useMemo(() => {
    switch (location.pathname) {
      case "/pulse":
        return "Pulse";
      case "/search":
        return "Browse";
      case "/backtest":
        return "Backtest";
      case "/portfolio":
        return "Portfolio";
      case "/bookmarks":
        return "Bookmarks";
      case "/settings":
        return "Settings";
      default:
        return "TradeCraft";
    }
  }, [location.pathname]);

  return (
    <div className="flex bg-surface-950 h-[100dvh] md:h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <Sidebar variant="desktop" />

      {/* Mobile drawer + backdrop */}
      {isMobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsMobileNavOpen(false)}
          className="fixed inset-0 z-40 bg-surface-950/70 backdrop-blur-sm md:hidden"
        />
      )}
      <Sidebar
        variant="mobile"
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-surface-950/85 backdrop-blur-xl border-b border-surface-800/50">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setIsMobileNavOpen(true)}
              className="p-2 -ml-2 rounded-lg bg-surface-900/50 border border-surface-800/60 text-surface-200 active:scale-[0.99]"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img
                src="/logo.svg"
                alt="TradeCraft"
                className="w-7 h-7 flex-shrink-0"
              />
              <span className="text-sm font-semibold text-surface-100 truncate">
                {pageTitle}
              </span>
            </div>
            
            {/* Theme button for mobile */}
            <MobileThemeButton />
            
            {/* Login button for unauthenticated users */}
            {!isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </header>

        <MfaWarningBanner />
        <WashSaleBanner />
        <AdminCustomBanner />
        <TokenRefreshBanner />

        <main className="flex-1 overflow-auto custom-scrollbar">
          <Outlet />
        </main>
        <Footer />
      </div>
      <HelpChat />
    </div>
  );
}
